import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Table, Typography } from 'antd';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { Button, Card, ProcessingLoader, Tab, TabList, TabPanel, TabPanels, Tabs } from '../../design-system';
import {
  getGheInputs,
  getGheResults,
  getGheUnseenPcaDiagnostics,
  runGheUnseenInference
} from '../../api/data';
import { GheResultsPayload } from '../../@types/gheResults/types';
import { RockId } from '../../@types/gheInputs/types';
import { GheRockResultsChart } from './GheRockResultsChart';
import { GheDataView } from './GheDataView';
import {
  PcaDiagnosticPoint,
  PcaDiagnosticsPayload
} from '../../@types/gheUnseenInference/types';
import ChartResults from '../chart/chartResults';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart.svg';
import styles from '../gheShared/gheStickyHeader.module.css';
import tableStyles from './gheResultsTable.module.css';

const ROCK_IDS: RockId[] = [
  'GHE1',
  'GHE2',
  'GHE3',
  'GHE4',
  'GHE5',
  'GHE6',
  'GHE7',
  'GHE8',
  'GHE9',
  'GHE10'
];

export const GheResultsTab = ({
  actions,
  embedded = false
}: {
  actions?: import('react').ReactNode;
  embedded?: boolean;
}) => {
  const { selectedInfo } = useSelected();
  const { configStates } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const goToGheInputs = () => {
    navigate({ pathname: '/ghe-inputs', search: location.search });
  };
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GheResultsPayload | null>(null);
  const [diagnostics, setDiagnostics] = useState<PcaDiagnosticsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeRockId, setActiveRockId] = useState<string>('');
  const [activeView, setActiveView] = useState<string>('pca');

  const diagnosticsColumns = [
    {
      title: 'Sample',
      dataIndex: 'sample_id',
      key: 'sample_id'
    },
    {
      title: 'Rock',
      dataIndex: 'rock_id',
      key: 'rock_id'
    },
    {
      title: 'PC1',
      dataIndex: 'pc1',
      key: 'pc1',
      render: (value: number) => Number(value).toFixed(6)
    },
    {
      title: 'PC2',
      dataIndex: 'pc2',
      key: 'pc2',
      render: (value: number) => Number(value).toFixed(6)
    },
    {
      title: 'Distance',
      dataIndex: 'distance_to_reference',
      key: 'distance_to_reference',
      render: (value?: number | null) =>
        value === null || value === undefined ? '-' : Number(value).toFixed(6)
    }
  ];

  const buildPcaSeries = (payload: PcaDiagnosticsPayload) => {
    const seriesFromPayload = Array.isArray(payload.plot_series)
      ? payload.plot_series
          .map((series: Record<string, any>, index: number) => {
            const rawData = Array.isArray(series?.data) ? series.data : [];
            const points = rawData
              .map((point: Record<string, any>) => ({
                x: Number(point?.x),
                y: Number(point?.y),
                sample_id: point?.sample_id,
                rock_id: point?.rock_id,
                source: point?.source,
                paper: point?.paper,
                formation: point?.formation,
                lithology: point?.lithology
              }))
              .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));

            if (points.length === 0) {
              return null;
            }

            const name = typeof series?.name === 'string' ? series.name : `series-${index + 1}`;
            const isReference = name.toLowerCase() === 'reference';
            const isUnseen = name.toLowerCase() === 'unseen';

            return {
              name: isUnseen ? 'Unseen samples' : isReference ? 'Reference cloud' : name,
              type: 'scatter' as const,
              data: points,
              color: isReference ? '#8c8c8c' : isUnseen ? '#1677ff' : undefined,
              marker: isReference
                ? {
                    enabled: true,
                    radius: 4,
                    symbol: 'circle',
                    fillColor: '#8c8c8c',
                    lineColor: '#595959',
                    lineWidth: 1
                  }
                : isUnseen
                ? {
                    enabled: true,
                    radius: 6,
                    symbol: 'circle',
                    fillColor: 'transparent',
                    lineColor: '#1677ff',
                    lineWidth: 2
                  }
                : undefined
            };
          })
          .filter(Boolean)
      : [];

    if (seriesFromPayload.length > 0) {
      return seriesFromPayload as {
        name: string;
        type: 'scatter';
        data: Array<{ x: number; y: number }>;
        color?: string;
      }[];
    }

    return [
      {
        name: 'Unseen samples',
        type: 'scatter' as const,
        data: payload.points.map(point => ({
          x: point.pc1,
          y: point.pc2
        })),
        color: '#1677ff'
      }
    ];
  };

  const getVarianceRatio = (payload: PcaDiagnosticsPayload | null, key: string): number | null => {
    const summary = payload?.reference_summary;
    if (!summary) {
      return null;
    }
    const value = Number(summary[key]);
    if (!Number.isFinite(value)) {
      return null;
    }
    return value;
  };

  const withVarianceLabel = (baseLabel: string, ratio: number | null) => {
    if (ratio === null) {
      return baseLabel;
    }
    return `${baseLabel} (${(ratio * 100).toFixed(1)}% variance)`;
  };

  useEffect(() => {
    const projectUid = selectedInfo.project?.uid;
    if (!projectUid) {
      setResults(null);
      return;
    }

    let isMounted = true;
    const loadResults = async () => {
      try {
        setLoading(true);
        setError(null);
        const [response, diagnosticsResponse] = await Promise.all([
          getGheResults(projectUid),
          Promise.resolve(getGheUnseenPcaDiagnostics(projectUid)).catch(() => null)
        ]);
        if (!isMounted) {
          return;
        }
        let resolvedDiagnostics = diagnosticsResponse;

        if (!resolvedDiagnostics || resolvedDiagnostics.points.length === 0) {
          const session = await getGheInputs(projectUid);
          const targetRocks = session?.target_rocks || [];
          const conditions = session?.reservoir_conditions;
          const unseenSamples =
            conditions && targetRocks.length > 0
              ? targetRocks
                  .filter(
                    rock =>
                      Number.isFinite(rock.porosity) &&
                      Number.isFinite(rock.permeability_md) &&
                      rock.porosity > 0 &&
                      rock.permeability_md > 0
                  )
                  .map(rock => ({
                    sample_id: `${rock.rock_id.toLowerCase()}-input`,
                    rock_id: rock.rock_id,
                    porosity: rock.porosity,
                    permeability_md: rock.permeability_md,
                    pressure_mpa: conditions.pressure_mpa,
                    temperature_c: conditions.temperature_c,
                    nacl_ppm: conditions.nacl_ppm,
                    mgcl2_ppm: conditions.mgcl2_ppm
                  }))
              : [];

          if (unseenSamples.length > 0) {
            const unseenResult = await runGheUnseenInference(projectUid, {
              samples: unseenSamples,
              include_pca_diagnostics: true,
              include_plot_payload: true
            });
            if (unseenResult?.pca_diagnostics) {
              resolvedDiagnostics = unseenResult.pca_diagnostics;
            }
          }
        }

        setResults(response);
        setDiagnostics(resolvedDiagnostics);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError('Unable to load GHE results. Please verify Page 1 inputs and try again.');
        setResults(null);
        setDiagnostics(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadResults();
    return () => {
      isMounted = false;
    };
  }, [selectedInfo.project?.uid, configStates.refreshCounter]);

  const availableResults = Array.isArray(results?.results)
    ? results?.results || []
    : Array.isArray((results as GheResultsPayload & { target_rocks?: GheResultsPayload['results'] })?.target_rocks)
    ? (results as GheResultsPayload & { target_rocks?: GheResultsPayload['results'] }).target_rocks || []
    : [];

  const pcaSeries = diagnostics ? buildPcaSeries(diagnostics) : [];
  const pcaYValues = pcaSeries.flatMap(series => series.data.map(point => point.y));
  const pcaXValues = pcaSeries.flatMap(series => series.data.map(point => point.x));
  const pcaMin = (values: number[]) => {
    if (values.length === 0) {
      return undefined;
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min;
    const padding = span > 0 ? span * 0.05 : 0.5;
    return min - padding;
  };
  const pcaMax = (values: number[]) => {
    if (values.length === 0) {
      return undefined;
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min;
    const padding = span > 0 ? span * 0.05 : 0.5;
    return max + padding;
  };
  const pcaXMin = pcaMin(pcaXValues);
  const pcaXMax = pcaMax(pcaXValues);
  const pcaYMin = pcaMin(pcaYValues);
  const pcaYMax = pcaMax(pcaYValues);
  const pc1Ratio = getVarianceRatio(diagnostics, 'pc1_explained_variance_ratio');
  const pc2Ratio = getVarianceRatio(diagnostics, 'pc2_explained_variance_ratio');
  const orderedAvailableResults = useMemo(
    () =>
      ROCK_IDS.map(rockId => availableResults.find(result => result.rock_id === rockId)).filter(
        Boolean
      ) as typeof availableResults,
    [availableResults]
  );
  const activeRockResult =
    availableResults.find(result => result.rock_id === activeRockId) ?? null;

  useEffect(() => {
    if (ROCK_IDS.length === 0) {
      setActiveRockId('');
      return;
    }

    const hasActiveRock = ROCK_IDS.includes(activeRockId as (typeof ROCK_IDS)[number]);
    if (!hasActiveRock) {
      setActiveRockId(ROCK_IDS[0]);
    }
  }, [activeRockId]);

  if (loading) {
    return <ProcessingLoader data-testid="ghe-results-loading" />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={error}
        showIcon
        data-testid="ghe-results-error"
      />
    );
  }

  if (!results || availableResults.length === 0) {
    return (
      <div
        data-testid="ghe-results-empty"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '48px 32px',
        }}
      >
        <EmptyChartIllustration style={{ width: 320 }} />
        <p style={{ margin: 0, fontSize: 'var(--ds-font-size-lg)', fontWeight: 500, color: 'var(--ds-emerald-zinc-700)' }}>
          No ML results yet
        </p>
        <p style={{ margin: 0, fontSize: 'var(--ds-font-size-md)', color: 'var(--ds-emerald-zinc-400)', textAlign: 'center', maxWidth: 340, lineHeight: 1.5 }}>
          Save GHE inputs on "GHE Inputs" page to see the results of the ML vs Analogues analysis.
        </p>
      </div>
    );
  }

  const rockResultsTabs = activeRockId ? (
    <Tabs value={activeRockId} onChange={setActiveRockId}>
      <TabList>
        {ROCK_IDS.map(rockId => (
          <Tab key={rockId} value={rockId}>
            {rockId}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {ROCK_IDS.map(rockId => {
          const result = availableResults.find(item => item.rock_id === rockId);

          return (
            <TabPanel key={rockId} value={rockId}>
              {result ? (
                <>
                  <GheRockResultsChart rockResult={result} />
                </>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  data-testid={`ghe-result-unavailable-${rockId}`}
                  message={
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                      <span>{`${rockId} results unavailable. Complete valid Page 1 inputs for this rock.`}</span>
                      <Button variant="secondary" onClick={goToGheInputs}>
                        Go to GHE Inputs
                      </Button>
                    </div>
                  }
                />
              )}
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  ) : null;

  const diagnosticsSection = diagnostics && diagnostics.points.length > 0 && (
        <div data-testid="ghe-results-unseen-diagnostics">
          {!embedded && (
            <Typography.Text type="secondary">
              Unseen PCA diagnostics loaded: {diagnostics.points.length} sample(s).
            </Typography.Text>
          )}
          <ChartResults
            containerWidth="100%"
            height="280px"
            backgroundColor="var(--ds-color-white)"
            borderColor="var(--ds-emerald-zinc-100)"
            title="Unseen Samples PCA Diagnostics"
            xAxis={withVarianceLabel('PC1', pc1Ratio)}
            yAxis={withVarianceLabel('PC2', pc2Ratio)}
            xAxisMin={pcaXMin}
            xAxisMax={pcaXMax}
            yAxisMin={pcaYMin}
            yAxisMax={pcaYMax}
            data={pcaSeries}
            tooltipFormatter={function tooltipFormatter() {
              const point = this.point as {
                x: number;
                y: number;
                sample_id?: string;
                rock_id?: string;
                paper?: string;
                formation?: string;
                lithology?: string;
              };
              const seriesName = this.series.name || 'Series';
              const lines: string[] = [];
              if (point.sample_id) lines.push(`<b>Sample:</b> ${point.sample_id}`);
              if (point.rock_id) lines.push(`<b>Rock:</b> ${point.rock_id}`);
              if (point.paper) lines.push(`<b>Reference:</b> ${point.paper}`);
              if (point.formation) lines.push(`<b>Formation:</b> ${point.formation}`);
              if (point.lithology) lines.push(`<b>Lithology:</b> ${point.lithology}`);
              const extra = lines.length > 0 ? `<br/>${lines.join('<br/>')}` : '';
              return `
                <b>${seriesName}</b><br/>
                <b>PC1:</b> ${Number(point.x).toFixed(6)}<br/>
                <b>PC2:</b> ${Number(point.y).toFixed(6)}
                ${extra}
              `;
            }}
          />
          <Table<PcaDiagnosticPoint>
            rowKey={record => `${record.rock_id}-${record.sample_id}`}
            columns={diagnosticsColumns}
            dataSource={diagnostics.points}
            size="small"
            style={{ marginTop: 12 }}
            pagination={false}
            data-testid="ghe-results-pca-table"
          />
        </div>
      );

  // Embedded (split screen): single card, parent pane handles scroll + padding,
  // so we skip the extra .tab/.content wrappers.
  if (embedded) {
    return (
      <Card fullWidth padding="md" data-testid="ghe-results-tab">
        <Tabs value={activeView} onChange={setActiveView}>
          <TabList>
            <Tab value="pca">PCA</Tab>
            <Tab value="data">Data</Tab>
          </TabList>
          <TabPanels>
            <TabPanel value="pca">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-lg)' }}>
                {diagnosticsSection}
                {rockResultsTabs}
              </div>
            </TabPanel>
            <TabPanel value="data">
              <div data-testid="ghe-results-data-tab">
                <GheDataView />
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Card>
    );
  }

  return (
    <div data-testid="ghe-results-tab" className={styles.tab}>
      <div className={styles.stickyHeader}>
        <Typography.Title level={3} style={{ margin: 0 }}>ML vs Analogues Results</Typography.Title>
        {actions}
      </div>
      <div className={styles.content}>
        {diagnosticsSection}
        {rockResultsTabs ? (
          <Card fullWidth padding="md">{rockResultsTabs}</Card>
        ) : null}
      </div>
    </div>
  );
};
